import { Component } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

/**
 * The floor under every render crash.
 *
 * WHY THIS EXISTS
 *
 * A React component that throws during render unmounts the whole tree above it
 * unless something catches the error — and nothing did. One block type the
 * canvas didn't recognise, one undefined import on a half-deployed server, and
 * the customer got a pure white page with the only explanation buried in a
 * minified console stack (React #130, the day this class was written).
 *
 * A white page and a broken page are different products. The broken page says
 * what happened, offers the two exits that always exist — reload, or go back —
 * and keeps the header/theme so it still looks like the app. The white page
 * says the product died.
 *
 * This is a class component because error boundaries have to be — hooks have
 * no equivalent of getDerivedStateFromError, deliberately.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The console is where a developer will look first; give them the real
    // component stack, which the minified error alone does not carry.
    // eslint-disable-next-line no-console
    console.error('Render crashed:', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Navigating away is an escape hatch — a crash on one route must not
    // follow the user to the next. `resetKey` is the route path.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ maxWidth: 460, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 42, mb: 1 }}>😵</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 0.75 }}>
            Something broke on this page
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13.5, mb: 2.5 }}>
            The rest of the app is fine — this one view hit an error it couldn’t recover from.
            Reloading usually clears it. If it keeps happening, tell us what you clicked.
          </Typography>
          <Stack direction="row" spacing={1.25} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => { window.history.back(); }}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
            >
              Go back
            </Button>
            <Button
              variant="contained"
              disableElevation
              startIcon={<RefreshRoundedIcon />}
              onClick={() => window.location.reload()}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, px: 3 }}
            >
              Reload
            </Button>
          </Stack>
          {import.meta.env.DEV && (
            <Box component="pre" sx={{ mt: 3, p: 1.5, textAlign: 'left', fontSize: 11, borderRadius: 2, bgcolor: '#0F172A', color: '#FCA5A5', overflow: 'auto', maxHeight: 200 }}>
              {String(this.state.error?.stack || this.state.error)}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
}
